import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  type AttendanceStatus,
  ATTENDANCE_STATUS_LABELS,
  ATTENDANCE_STATUS_COLORS,
  markAttendance,
} from "@/hooks/useAttendance";

interface AttendanceStudent {
  userId: string;
  name: string;
}

interface AttendanceModalProps {
  open: boolean;
  onClose: () => void;
  liveClassId: string;
  className?: string;
  students: AttendanceStudent[];
  initial?: Record<string, AttendanceStatus>;
}

const STATUS_ORDER: AttendanceStatus[] = ["present", "absent", "late"];

export default function AttendanceModal({
  open,
  onClose,
  liveClassId,
  className = "",
  students,
  initial = {},
}: AttendanceModalProps) {
  const [statuses, setStatuses] = useState<Record<string, AttendanceStatus>>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStatuses(initial);
      setError(null);
    }
  }, [open, initial]);

  const setStatus = (userId: string, status: AttendanceStatus) => {
    setStatuses((prev) => ({ ...prev, [userId]: status }));
  };

  const handleSave = async () => {
    setError(null);
    if (students.length === 0) return;
    const records = students.map((s) => ({
      studentUserId: s.userId,
      status: statuses[s.userId] ?? "present",
    }));
    setBusy(true);
    try {
      await markAttendance(liveClassId, records);
      onClose();
    } catch {
      setError("Yoklama kaydedilirken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const presentCount = students.filter((s) => (statuses[s.userId] ?? "present") === "present").length;
  const absentCount = students.filter((s) => (statuses[s.userId] ?? "present") === "absent").length;
  const lateCount = students.filter((s) => (statuses[s.userId] ?? "present") === "late").length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Yoklama Al"
      titleId="attendance-modal-title"
      id="attendance-modal"
      maxWidthClass="max-w-lg"
    >
      <div className="space-y-4">
        {className && (
          <p className="text-sm text-foreground-600">
            <span className="font-semibold text-foreground-900">{className}</span> dersi için öğrencilerin katılımını işaretle.
          </p>
        )}

        {/* Özet */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-semibold">
            <i className="ri-check-line" aria-hidden="true"></i>
            {presentCount} Katıldı
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 text-red-700 text-xs font-semibold">
            <i className="ri-close-line" aria-hidden="true"></i>
            {absentCount} Katılmadı
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-semibold">
            <i className="ri-time-line" aria-hidden="true"></i>
            {lateCount} Geç Kaldı
          </span>
        </div>

        {students.length === 0 ? (
          <p className="text-sm text-foreground-500 bg-background-100 rounded-md px-3 py-4 text-center">
            Bu derse atanmış öğrenci yok.
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto divide-y divide-background-200 rounded-lg border border-background-200">
            {students.map((s) => {
              const status = statuses[s.userId] ?? "present";
              return (
                <div key={s.userId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <span className="text-sm text-foreground-800 truncate">{s.name}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {STATUS_ORDER.map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setStatus(s.userId, st)}
                        className={`h-8 px-2.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                          status === st
                            ? ATTENDANCE_STATUS_COLORS[st]
                            : "text-foreground-400 hover:bg-background-100"
                        }`}
                      >
                        {ATTENDANCE_STATUS_LABELS[st]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || students.length === 0}
            className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            {busy ? "Kaydediliyor..." : "Yoklamayı Kaydet"}
          </button>
        </div>
      </div>
    </Modal>
  );
}